// ---------------------------------------------------------------------------
// Tutorial v3 — the steps (the progression prototype's first war)
// ---------------------------------------------------------------------------
//
// tutorialV3.js's five rounds as steps for v2's step machine
// (tutorialV2Steps.makeTutorialMachine; the step kinds are documented there).
// The first attack (a swipe slide, then a tap slide), the brood and the move
// carry `gesture`: the board shows a white glove doing it
// (components/v2/hex/GestureHand.js; corridors 0-based).
// It covers deploying, attacking and aiming, reading clues with the eye,
// protecting your Outpost, building a brood, and moving into a corridor to
// attack from it in the same round. Much of the copy is v2's.

import { makeTutorialMachine, fresh, deploysDone, aimed, stampAt, letterDistance } from './tutorialV2Steps'
import { subIndex } from '../rules/subplotGrid'
import { TUTORIAL_V3_TARGETS, TUTORIAL_V3_SELF_OUTPOST, TUTORIAL_V3_CAPACITIES, TUTORIAL_V3_CONFIG, createTutorialV3War, playTutorialV3RoundWith } from './tutorialV3'

export const TUTORIAL_V3_ROUNDS = 5

export const TUTORIAL_V3_STEPS = [
    // ── R1 ──
    { id: 'hive', round: 1, kind: 'next', selfXray: true, outlineHive: true, hiveArrow: true, cellArrow: TUTORIAL_V3_SELF_OUTPOST,
      text: 'This is your Hive.\n\nThe blue hexagon is your Outpost: your one structure in this war.' },
    { id: 'hidden', round: 1, kind: 'next',
      text: 'Find your opponent’s hidden Outpost before they find yours.' },
    { id: 'corridors', round: 1, kind: 'next', sweepColumns: true,
      text: 'The board is split into 7 corridors: vertical columns that run straight through your Hive and on through theirs.' },
    { id: 'airspace', round: 1, kind: 'next', sweepCorridors: 'self',
      text: 'Each corridor has airspace, the lane where your drones can fly. Longer lanes hold more drones.' },
    { id: 'deploy1', round: 1, kind: 'act', highlight: [3, 4], pulsePlus: true, deploy: [3, 4], done: deploysDone,
      text: 'You get 2 drones to deploy each round. Tap the white plus in corridors 3 and 4 to deploy.' },
    { id: 'notYet', round: 1, kind: 'next',
      text: 'Drones can only attack from a corridor they started the round in. These just arrived, so they can’t yet attack.' },
    { id: 'submit1', round: 1, kind: 'submit',
      text: 'Tap Submit to end the round.' },

    // ── R2 ──
    { id: 'paid', round: 2, kind: 'next',
      text: 'Round 2.\nWhen you submitted, your drones flew up into the airspace in corridors 3 and 4.' },
    { id: 'noAttacks', round: 2, kind: 'next',
      text: 'No one can attack until round 3. Use this round to build up your forces.' },
    { id: 'deploy2', round: 2, kind: 'act', highlight: [7], pulsePlus: true, deploy: [7, 7], done: deploysDone,
      text: 'Remember, you get 2 drones to deploy each round. Deploy both new drones to corridor 7.' },
    { id: 'submit2', round: 2, kind: 'submit',
      text: 'Submit.' },

    // ── R3 ──
    { id: 'attacksOpen', round: 3, kind: 'next', highlight: [3, 4, 7],
      text: 'Round 3: attacks are open.\nDrones can attack from the corridor they started the round in, so you can attack from corridors 3, 4 and 7.' },
    { id: 'theirRule', round: 3, kind: 'next', highlight: [1, 2, 5, 6],
      text: 'The same goes for your opponent. They can only attack from corridors 1, 2, 5 and 6, where their drones started this round.' },
    { id: 'swipe', round: 3, kind: 'next', highlight: [3], gesture: { kind: 'swipe', col: 2 },
      text: 'To attack from a corridor, swipe up on it.' },
    { id: 'attack3', round: 3, kind: 'act', highlight: [3], attack: 3, done: (s) => s.board[2].isAttacking,
      gesture: { kind: 'tap', col: 2 },
      text: 'Or tap the red arrow at its top.\n\nAttack from corridor 3 now: swipe up, or tap its arrow.' },
    { id: 'aim3', round: 3, kind: 'act', highlight: [3], aims: 'probe1', aimCorridor: 3, done: aimed(3), lowerPanel: true,
      text: 'When you attack, all your drones in that corridor fly up and sting the target location. If your opponent doesn’t defend, the attack will be successful.\n\nChoose your target: tap the hex where the yellow arrow is pointing.' },
    { id: 'attack4', round: 3, kind: 'act', highlight: [4], attack: 4, done: (s) => s.board[3].isAttacking,
      text: 'You can attack from more than one corridor in a round. Attack from corridor 4 as well.' },
    { id: 'aim4', round: 3, kind: 'act', highlight: [4], aims: 'probe2', aimCorridor: 4, done: aimed(4),
      text: 'Tap to target the hex-plot the arrow points to.' },
    { id: 'deploy3', round: 3, kind: 'act', highlight: [5, 6], pulsePlus: true, deploy: [5, 6], done: deploysDone,
      text: 'Deploy to corridors 5 and 6.' },
    { id: 'submit3', round: 3, kind: 'submit',
      text: 'Submit.' },
    { id: 'struck', round: 3, kind: 'next',
      text: 'Your opponent attacked in corridor 2. You had no drones there to stop them, so they hit your Hive.\n\nThey missed your Outpost, but now they have a clue where it is.' },
    { id: 'probe1', round: 3, kind: 'next',
      text: (s) => `Both your attacks were successful. You missed their Outpost, but each strike left a clue.\n\n${letterDistance(s.war.p2.homefront, s.aims.probe1) ?? 'A:6'} means their Outpost is ${stampAt(s.war.p2.homefront, s.aims.probe1)?.distance ?? 6} hex-plots from where you hit in corridor 3. ${letterDistance(s.war.p2.homefront, s.aims.probe2) ?? 'A:3'} means it’s ${stampAt(s.war.p2.homefront, s.aims.probe2)?.distance ?? 3} from where you hit in corridor 4.` },
    { id: 'peek1', round: 3, kind: 'peek', eyeArrow: true,
      text: 'Press and hold the eye button at the top to see the clues.' },
    { id: 'ring1', round: 3, kind: 'next',
      text: 'Each clue tells you how far away the enemy Outpost is, so it must be somewhere on that ring.\n\nPress and hold the eye button anytime to see all your clues.' },
    { id: 'cross', round: 3, kind: 'next',
      text: 'Their Outpost is on both rings, so it must be where the rings cross. They only cross at one location — in corridor 5.' },

    // ── R4 ──
    { id: 'block', round: 4, kind: 'act', highlight: [1], pulsePlus: true, deploy: [1], done: (s) => fresh(s, 1) === 1,
      text: 'Round 4. They have a drone in corridor 1, right over your Outpost, so they can attack it this round.\n\nProtect your Outpost: deploy a drone to corridor 1. New drones defend straight away.' },
    { id: 'defended', round: 4, kind: 'next', highlight: [5],
      text: 'They also have a drone in corridor 5, where you know their Outpost is.\n\nYour 1 drone versus their 1 drone would be a wash, which strikes nothing. You need more drones.' },
    { id: 'moreDrones', round: 4, kind: 'next',
      text: 'Broods produce more drones. It takes 2 flying (orange) drones in the same corridor to build a brood.' },
    { id: 'brood', round: 4, kind: 'act', highlight: [7], brood: 7, done: (s) => (s.board[6].pendingBaseBuilds ?? []).length === 1,
      gesture: { kind: 'brood', col: 6, centerS: subIndex(19, 4) },
      text: 'To build a brood, drag a drone in corridor 7 onto the other, then select where the brood should be built in the corridor. The drones will fly down and start to build.' },
    { id: 'broodInfo', round: 4, kind: 'next',
      text: 'While your brood is being built, it’s vulnerable — the enemy can see it, a single hit can destroy it, and it can’t attack.\n\nOnce complete, it’s worth 2 drones in the corridor, and produces 1 new airdropped drone every round.' },
    { id: 'deploy4', round: 4, kind: 'act', highlight: [3], pulsePlus: true, deploy: [1, 3], done: deploysDone,
      text: 'Deploy your last drone to corridor 3.' },
    { id: 'submit4', round: 4, kind: 'submit',
      text: 'Submit.' },
    { id: 'tie', round: 4, kind: 'next',
      text: 'They attacked corridor 1: 1 against 1 is a wash, so no damage was caused to your Hive.\n\nYour Outpost is safe.' },

    // ── R5 ──
    { id: 'move', round: 5, kind: 'act', highlight: [6], move: { from: 6, dir: 'left' }, done: (s) => s.board[5].movedOut,
      gesture: { kind: 'move', from: 5, to: 4 },
      text: 'Round 5. Drag the flying drone in corridor 6 left into corridor 5. Drones that move can still attack this round.' },
    { id: 'oneMove', round: 5, kind: 'next',
      text: 'Each round, only 1 drone can move from each corridor, left or right.' },
    { id: 'attack5', round: 5, kind: 'act', highlight: [5], attack: 5, done: (s) => s.board[4].isAttacking,
      text: 'Corridor 5 is 2 against 1 now. Attack from it.' },
    { id: 'aim5', round: 5, kind: 'act', highlight: [5], aims: 'kill', aimCorridor: 5, done: aimed(5),
      text: 'Select your aim. Use the eye button again to see where your clues overlap.' },
    { id: 'deploy5', round: 5, kind: 'act', highlight: [3, 4], pulsePlus: true, deploy: [3, 4], done: deploysDone,
      text: 'Deploy to corridors 3 and 4.' },
    { id: 'submit5', round: 5, kind: 'submit',
      text: 'Submit.' },
    { id: 'won', round: 5, kind: 'end',
      text: 'You destroyed their Outpost and won the war!' },
]

export const TUTORIAL_V3_MACHINE = makeTutorialMachine({
    steps: TUTORIAL_V3_STEPS, targets: TUTORIAL_V3_TARGETS, createWar: createTutorialV3War, playRound: playTutorialV3RoundWith,
})

// What the tutorial board (components/TutorialV2Board) plays.
export const TUTORIAL_V3 = {
    machine: TUTORIAL_V3_MACHINE,
    capacities: TUTORIAL_V3_CAPACITIES,
    rounds: TUTORIAL_V3_ROUNDS,
    structuresToWin: TUTORIAL_V3_CONFIG.structuresToWin,
    intro: 'Two hidden Hives at war. First to destroy the other’s Outpost wins.',
    scoreDemo: false,
    endButton: 'Claim War Chest',
}
